import type { ApiClient } from './client';
import { applyStripeKeyOverride } from '../domain/payments';
import type {
  BookingFormConfig,
  BookingPolicy,
  CustomField,
  Frequency,
  LocationPolicy,
  Service,
  TenantSettings,
} from './types';

/** Everything the form needs before it can render — fetched in parallel on load. */
export interface BootstrapData {
  settings: TenantSettings;
  form: BookingFormConfig;
  services: Service[];
  frequencies: Frequency[];
  customFields: CustomField[];
  bookingPolicy: BookingPolicy;
  locationPolicy: LocationPolicy;
}

export interface BootstrapOptions {
  settingsPreload?: TenantSettings | null;
  /** sandbox-only Stripe publishable key override (see domain/payments.ts) */
  stripeKeyOverride?: string | null;
  env?: 'sandbox' | 'production';
}

export async function loadBootstrap(
  client: ApiClient,
  formType: string,
  signal?: AbortSignal,
  settingsPreload: TenantSettings | null = null,
  options: BootstrapOptions = {},
): Promise<BootstrapData> {
  const [
    fetchedSettings,
    form,
    services,
    frequencies,
    customFields,
    bookingPolicy,
    locationPolicy,
  ] = await Promise.all([
    client.getSettings(signal),
    client.getBookingForm(formType, signal),
    client.getServices(signal),
    client.getFrequencies(signal),
    client.getCustomFields(signal),
    client.getBookingPolicy(signal),
    client.getLocationPolicy(signal),
  ]);
  // The sandbox tenant answers anonymous requests with an empty body (vendor bug); the plugin
  // can inline a server-fetched copy for exactly that case.
  const settings =
    fetchedSettings && typeof fetchedSettings === 'object' && Object.keys(fetchedSettings).length
      ? fetchedSettings
      : settingsPreload;
  if (!settings) throw new Error('Tenant settings are unavailable');
  return {
    settings: applyStripeKeyOverride(settings, options.stripeKeyOverride, options.env ?? 'sandbox'),
    form,
    services,
    frequencies,
    customFields,
    bookingPolicy,
    locationPolicy,
  };
}
